import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Image from 'next/image'
import Link from 'next/link'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { RegularSection } from '@/components/Layouts'
import { getImageData } from '@/utilites/getImageData'
import RichText from '@/components/RichText'
import { resolveHref } from '@/utilites/resolveHref'

interface Card {
  image?: unknown
  text?: DefaultTypedEditorState
  enableLink?: boolean
  link?: CardLink
}

type CardLink = {
  type?: 'custom' | 'reference' | null
  newTab?: boolean | null
  reference?: string | { slug?: string | null } | null
  url?: string | null
}

interface Content {
  title?: DefaultTypedEditorState
  cards?: Card[]
}

interface S2_about_4Props {
  content?: Content
  blockName?: string
}

const Index = (props: S2_about_4Props) => {
  const { content = {} } = props
  const { title, cards = [] } = content

  return (
    <RegularSection aria-label='S2_about_4'>
      <Grid container spacing={4}>
        <Grid size={12}>
          {title ? (
            <Box
              sx={{
                '& p': {
                  m: 0,
                  textAlign: 'start',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  fontWeight: 700,
                  lineHeight: 1.2,
                },
              }}
            >
              <RichText data={title} enableGutter={false} enableProse={false} />
            </Box>
          ) : null}
        </Grid>

        {cards.map((card, idx) => {
          const imageData = getImageData(card?.image)
          const href = card?.enableLink && (
            card?.link?.type === 'custom'
              ? (card.link.url ? resolveHref(card.link.url) : null)
              : card?.link?.type === 'reference'
                ? (typeof card.link.reference === 'object' && card.link.reference?.slug
                  ? resolveHref(card.link.reference.slug)
                  : null)
                : null
          )

          const inner = (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                borderRadius: 5,
                gap: 2,
                border: '1px solid var(--primary-color)',
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  flex: 1,
                  gap: 1,
                }}
              >
                {card?.text ? (
                  <Box
                    sx={{
                      '& p': {
                        m: 0,
                        fontSize: '1rem',
                        lineHeight: 1.5,
                      },
                    }}
                  >
                    <RichText data={card.text} enableGutter={false} enableProse={false} />
                  </Box>
                ) : null}
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  width: 200,
                  height: 140,
                  minWidth: 140,
                  flexShrink: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: imageData.src ? 'transparent' : 'rgba(0, 0, 0, 0.05)',
                }}
              >
                {imageData.src ? (
                  <Image
                    src={imageData.src}
                    alt={imageData.alt || ''}
                    fill
                    sizes='200px'
                    style={{ objectFit: 'cover' }}
                    loading='lazy'
                  />
                ) : null}
              </Box>
            </Box>
          )

          return (
            <Grid size={{ xs: 12, md: 6, lg: 3 }} key={'S2_about_4' + idx}>
              {href ? (
                <Link
                  href={href}
                  target={card?.link?.newTab ? '_blank' : undefined}
                  rel={card?.link?.newTab ? 'noopener noreferrer' : undefined}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  {inner}
                </Link>
              ) : (
                inner
              )}
            </Grid>
          )
        })}
      </Grid>
    </RegularSection>
  )
}

export default Index
